/**
 * File Thumbnail Component
 *
 * Displays thumbnails for different file types:
 * - Images: actual thumbnail
 * - PDFs: PDF icon
 * - Videos: video icon with play button
 * - Office docs: appropriate icon
 * - Other files: generic file icon
 */

import { useState, useEffect, useMemo } from 'react';
import {
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  AudioOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import apiClient from '@api/client';

interface FileThumbnailProps {
  file: {
    original_name: string;
    mime_type: string;
    url: string;
  };
  size?: number;
  showIcon?: boolean;
}

export default function FileThumbnail({
  file,
  size = 48,
  showIcon = true,
}: FileThumbnailProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const ext = file.original_name.split('.').pop()?.toLowerCase() || '';
  const mimeType = file.mime_type.toLowerCase();

  // Transform relative URL to full worker URL (not /api endpoint)
  const fullUrl = useMemo(() => {
    if (file.url.startsWith('http://') || file.url.startsWith('https://')) {
      return file.url;
    }
    // Remove /api suffix as files are served from worker root
    const baseUrl = (apiClient.defaults.baseURL || '').replace(/\/api$/, '');
    return file.url.startsWith('/') ? `${baseUrl}${file.url}` : `${baseUrl}/${file.url}`;
  }, [file.url]);

  // Determine if it's an image
  const isImage = mimeType.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);

  // Reset image state when file changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [file.url]);

  // Get appropriate icon based on file type
  const getFileIcon = () => {
    const iconStyle = { fontSize: size, color: getFileColor() };

    // PDFs
    if (ext === 'pdf' || mimeType === 'application/pdf') {
      return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
    }

    // Word documents
    if (['doc', 'docx'].includes(ext) || mimeType.includes('msword') || mimeType.includes('wordprocessing')) {
      return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
    }

    // Excel spreadsheets
    if (['xls', 'xlsx', 'csv'].includes(ext) || mimeType.includes('spreadsheet') || mimeType.includes('ms-excel')) {
      return <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
    }

    // PowerPoint presentations
    if (['ppt', 'pptx'].includes(ext) || mimeType.includes('presentation') || mimeType.includes('ms-powerpoint')) {
      return <FilePptOutlined style={{ ...iconStyle, color: '#fa8c16' }} />;
    }

    // Images
    if (isImage) {
      return <FileImageOutlined style={{ ...iconStyle, color: '#722ed1' }} />;
    }

    // Videos
    if (mimeType.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext)) {
      return <PlayCircleOutlined style={{ ...iconStyle, color: '#eb2f96' }} />;
    }

    // Audio
    if (mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext)) {
      return <AudioOutlined style={{ ...iconStyle, color: '#13c2c2' }} />;
    }

    // Archives
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <FileZipOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />;
    }

    // Text/code files
    if (mimeType.startsWith('text/') || ['txt', 'md', 'json', 'xml', 'js', 'ts', 'css', 'html'].includes(ext)) {
      return <FileTextOutlined style={{ ...iconStyle, color: '#595959' }} />;
    }

    // Default generic file icon
    return <FileOutlined style={iconStyle} />;
  };

  const getFileColor = () => {
    const colorMap: Record<string, string> = {
      pdf: '#ff4d4f',
      doc: '#1890ff',
      docx: '#1890ff',
      xls: '#52c41a',
      xlsx: '#52c41a',
      csv: '#52c41a',
      ppt: '#fa8c16',
      pptx: '#fa8c16',
      jpg: '#722ed1',
      jpeg: '#722ed1',
      png: '#722ed1',
      gif: '#722ed1',
      mp4: '#eb2f96',
      mp3: '#13c2c2',
      zip: '#8c8c8c',
      rar: '#8c8c8c',
    };
    return colorMap[ext] || '#1890ff';
  };

  // If it's an image, try to load it as a thumbnail
  if (isImage && !imageError) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          background: '#f5f5f5',
        }}
      >
        {!imageLoaded && (
          <Spin size="small" />
        )}
        <img
          src={fullUrl}
          alt={file.original_name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: imageLoaded ? 'block' : 'none',
          }}
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(false);
          }}
        />
      </div>
    );
  }

  // For non-images or failed image loads, show appropriate icon
  if (showIcon) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {getFileIcon()}
      </div>
    );
  }

  // Just return the icon without wrapper
  return getFileIcon();
}
